import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, AlertCircle, ShoppingBag, Users, Package, DollarSign, Download, Save, Pencil, CalendarX, Search, Calendar, Send, Cake, X, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(cents, currency) {
  const value = (cents || 0) / 100;
  const code = (currency || "usd").toUpperCase();
  const hasCents = value % 1 !== 0;
  const formatted = value.toLocaleString("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  });
  return `${code} $${formatted}`;
}

function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-3 font-serif text-3xl text-brand-blue">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status, refunded }) {
  const s = (refunded ? "refunded" : (status || "-")).toLowerCase();
  const map = {
    paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
    completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
    initiated: "bg-amber-50 text-amber-800 border-amber-200",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    refunded: "bg-slate-100 text-slate-700 border-slate-300",
    canceled: "bg-slate-100 text-slate-600 border-slate-300",
    canceling: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <span data-testid={`status-badge-${s}`} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border ${map[s] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
      {s === "paid" && <CheckCircle2 className="w-3 h-3" />}
      {s === "refunded" && <AlertCircle className="w-3 h-3" />}
      {s}
    </span>
  );
}

/* Inline notes editor (used for both order notes and private member notes) */
function NotesCell({ initialValue, endpoint, testIdPrefix, placeholder, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(initialValue || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setVal(initialValue || ""); }, [initialValue]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(endpoint, { note: val });
      toast.success("Note saved");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note.");
    } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          data-testid={`${testIdPrefix}-input`}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setVal(initialValue || ""); } }}
          className="border border-slate-300 px-2 py-1 text-xs w-56 focus:outline-none focus:border-brand-blue"
          autoFocus
        />
        <button data-testid={`${testIdPrefix}-save`} onClick={save} disabled={busy} className="text-xs uppercase tracking-widest font-semibold text-brand-orange hover:text-brand-blue disabled:opacity-50">
          {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <Save className="w-3 h-3 inline" />}
        </button>
      </div>
    );
  }
  return (
    <button
      data-testid={`${testIdPrefix}-cell`}
      onClick={() => setEditing(true)}
      className="text-left w-full text-xs text-slate-600 hover:text-brand-orange group inline-flex items-start gap-1"
    >
      <span className="flex-1">{initialValue || <span className="italic text-slate-400 group-hover:text-brand-orange">{placeholder}</span>}</span>
      <Pencil className="w-3 h-3 mt-0.5 opacity-0 group-hover:opacity-100 flex-shrink-0" />
    </button>
  );
}

/* Inline datetime-local editor for coaching session scheduling */
function ScheduleCell({ order, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(isoToLocalInput(order.scheduled_at));
  const [busy, setBusy] = useState(false);

  useEffect(() => { setVal(isoToLocalInput(order.scheduled_at)); }, [order.scheduled_at]);

  const save = async (raw) => {
    setBusy(true);
    try {
      const iso = raw ? new Date(raw).toISOString() : null;
      await axios.patch(`${API}/admin/orders/${order.id}/scheduled-at`, { scheduled_at: iso });
      toast.success(iso ? "Session scheduled" : "Schedule cleared");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule.");
    } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          data-testid={`schedule-input-${order.id}`}
          type="datetime-local"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:border-brand-blue"
          autoFocus
        />
        <button data-testid={`schedule-save-${order.id}`} onClick={() => save(val)} disabled={busy} className="text-xs uppercase tracking-widest font-semibold text-brand-orange">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
        </button>
        {order.scheduled_at && <button data-testid={`schedule-clear-${order.id}`} onClick={() => save("")} className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-red-600">Clear</button>}
      </div>
    );
  }
  return (
    <button data-testid={`schedule-cell-${order.id}`} onClick={() => setEditing(true)} className="text-left text-xs text-slate-600 hover:text-brand-orange inline-flex items-center gap-1">
      <Calendar className="w-3 h-3" />
      {order.scheduled_at ? new Date(order.scheduled_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : <span className="italic text-slate-400">Schedule…</span>}
    </button>
  );
}

export default function AdminOrders() {
  const { user, status: authStatus } = useAuth();
  const [state, setState] = useState({ loading: true, error: null });
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [busy, setBusy] = useState({});
  const [digestPreview, setDigestPreview] = useState(null); // {subject, html, summary, recipient} | null
  const [previewLoading, setPreviewLoading] = useState(false);
  // Filters
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [kind, setKind] = useState("all");

  const load = () => {
    setState({ loading: true, error: null });
    axios.get(`${API}/admin/orders`)
      .then(({ data }) => {
        setOrders(data.orders || []);
        setSubs(data.subscriptions || []);
        setState({ loading: false, error: null });
      })
      .catch((e) => {
        setState({ loading: false, error: e?.response?.data?.detail || "Could not load orders." });
      });
    // Renewals is a separate call because it hits Stripe (slower).
    axios.get(`${API}/admin/renewals?days=30`)
      .then(({ data }) => setRenewals(data.renewals || []))
      .catch(() => {});
  };

  const openDigestPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/digest/preview`);
      setDigestPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load digest preview.");
    } finally { setPreviewLoading(false); }
  };

  const sendDigestNow = async () => {
    setBusy((b) => ({ ...b, digest: true }));
    try {
      const { data } = await axios.post(`${API}/admin/digest/send-now`);
      toast.success(`Digest queued to ${data.recipient}`);
      setDigestPreview(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send digest."); }
    finally { setBusy((b) => ({ ...b, digest: false })); }
  };

  const sendRenewalNudge = async (r) => {
    if (!window.confirm(`Send a personal renewal thank-you to ${r.buyer_email}?`)) return;
    const key = `nudge-${r.session_id}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const { data } = await axios.post(`${API}/admin/renewals/${r.session_id}/nudge`);
      toast.success(`Thank-you sent to ${data.sent_to}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); }
    finally { setBusy((b) => ({ ...b, [key]: false })); }
  };

  useEffect(() => {
    if (authStatus === "authed" && user?.role === "admin") load();
  }, [authStatus, user]);

  const filterFn = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const fromTs = from ? new Date(from + "T00:00:00").getTime() : null;
    const toTs = to ? new Date(to + "T23:59:59").getTime() : null;
    return (item, isSub) => {
      const created = item.created_at ? new Date(item.created_at).getTime() : 0;
      if (fromTs && created < fromTs) return false;
      if (toTs && created > toTs) return false;
      if (needle) {
        const hay = [
          item.product_title, item.buyer_name, item.buyer_email, item.id, item.session_id,
          item.product_kind, item.lookup_key, item.admin_note, item.member_note,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (kind === "book" && isSub) return false;
      if (kind === "book" && !isSub && item.product_kind !== "book") return false;
      if (kind === "coaching" && (isSub || item.product_kind !== "coaching")) return false;
      if (kind === "subscription" && !isSub) return false;
      return true;
    };
  }, [q, from, to, kind]);

  const filteredOrders = useMemo(() => orders.filter((o) => filterFn(o, false)), [orders, filterFn]);
  const filteredSubs = useMemo(() => subs.filter((s) => filterFn(s, true)), [subs, filterFn]);

  const upcomingCoaching = useMemo(() => {
    const nowTs = Date.now();
    return orders
      .filter((o) => o.product_kind === "coaching" && o.scheduled_at && new Date(o.scheduled_at).getTime() >= nowTs && !o.refunded)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
      .slice(0, 10);
  }, [orders]);

  if (authStatus === "checking") return <div className="min-h-[60vh] flex items-center justify-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking session…</div>;
  if (authStatus !== "authed") return <Navigate to="/login" replace />;
  if (user?.role !== "admin") return <Navigate to="/member/hub" replace />;

  const paidOrders = orders.filter((o) => o.payment_status === "paid" && !o.refunded);
  const revenueByCurrency = {};
  for (const o of paidOrders) {
    const c = (o.currency || "usd").toUpperCase();
    revenueByCurrency[c] = (revenueByCurrency[c] || 0) + (o.amount_cents || 0);
  }
  const revenueLabel = Object.entries(revenueByCurrency).map(([c, cents]) => `${c} $${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`).join(" · ") || "-";
  const activeSubs = subs.filter((s) => ["active", "canceling"].includes(s.subscription_status) && !s.refunded);
  const bookOrders = orders.filter((o) => o.product_kind === "book");
  const coachOrders = orders.filter((o) => o.product_kind === "coaching");

  const refundOrder = async (order) => {
    if (!window.confirm(`Refund ${order.product_title} to ${order.buyer_email || "buyer"} for ${money(order.amount_cents, order.currency)}? This is irreversible.`)) return;
    setBusy((b) => ({ ...b, [order.id]: true }));
    try {
      const { data } = await axios.post(`${API}/admin/orders/${order.id}/refund`);
      toast.success(`Refunded ${money(data.amount_refunded, order.currency)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed."); }
    finally { setBusy((b) => ({ ...b, [order.id]: false })); }
  };

  const refundSub = async (sub) => {
    if (!window.confirm(`Refund subscription for ${sub.buyer_email || "member"} (${money(sub.amount, sub.currency)}) AND cancel their membership immediately?`)) return;
    setBusy((b) => ({ ...b, [sub.session_id]: true }));
    try {
      const { data } = await axios.post(`${API}/admin/subscriptions/${sub.session_id}/refund`);
      toast.success(`Refunded ${money(data.amount_refunded, sub.currency)} and cancelled subscription`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed."); }
    finally { setBusy((b) => ({ ...b, [sub.session_id]: false })); }
  };

  const cancelAtPeriodEnd = async (sub) => {
    if (!window.confirm(`Cancel ${sub.buyer_email || "member"}'s subscription at period end? They keep access until the current period expires and no refund is issued.`)) return;
    const key = `cape-${sub.session_id}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await axios.post(`${API}/admin/subscriptions/${sub.session_id}/cancel-at-period-end`);
      toast.success("Scheduled cancellation at period end");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule cancellation."); }
    finally { setBusy((b) => ({ ...b, [key]: false })); }
  };

  const downloadCsv = () => {
    fetch(`${API}/admin/orders/export.csv`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("Export failed."); return r.blob(); })
      .then((blob) => {
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = `atsi-orders-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(href), 3000);
      })
      .catch(() => toast.error("Could not download CSV."));
  };

  const clearFilters = () => { setQ(""); setFrom(""); setTo(""); setKind("all"); };
  const filtered = q || from || to || kind !== "all";

  return (
    <div data-testid="admin-orders-page" className="bg-slate-50 min-h-screen py-14 px-6 lg:px-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-start justify-between mb-8 gap-4">
          <div>
            <div className="eyebrow text-brand-orange mb-2">Admin</div>
            <h1 className="font-serif text-4xl lg:text-5xl text-brand-blue leading-tight">Orders & memberships.</h1>
            <p className="text-slate-600 mt-3 max-w-2xl">Every paid book, coaching booking and Member Hub subscription in one place. Search, refund, schedule sessions and leave private notes.</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-send-digest-btn" onClick={openDigestPreview} disabled={previewLoading} className="btn-secondary whitespace-nowrap disabled:opacity-50">
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send digest now
            </button>
            <button data-testid="admin-csv-export-btn" onClick={downloadCsv} className="btn-secondary whitespace-nowrap"><Download className="w-4 h-4" /> Download CSV</button>
            <button data-testid="admin-refresh-btn" onClick={load} className="btn-secondary whitespace-nowrap"><RefreshCw className="w-4 h-4" /> Refresh</button>
          </div>
        </div>

        {state.error && <div className="mb-8 border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-900">{state.error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={DollarSign} label="Shop revenue (net of refunds)" value={revenueLabel} sub={`${paidOrders.length} paid shop orders`} />
          <StatCard icon={Users} label="Active members" value={activeSubs.length} sub="Member Hub subscriptions" />
          <StatCard icon={Package} label="Book orders" value={bookOrders.length} />
          <StatCard icon={ShoppingBag} label="Coaching bookings" value={coachOrders.length} />
        </div>

        {renewals.length > 0 && (
          <section data-testid="admin-renewals" className="mb-10 bg-white border-l-4 border-brand-orange border-t border-r border-b border-slate-200 p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-brand-orange mb-3"><Cake className="w-3.5 h-3.5" /> Renewals due in the next 30 days</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {renewals.map((r) => (
                <div key={r.session_id} data-testid={`renewal-${r.session_id}`} className="border border-slate-200 p-4 rounded-sm">
                  <div className="font-serif text-lg text-brand-blue">{r.days_until_renewal === 0 ? "Renews today" : `Renews in ${r.days_until_renewal} day${r.days_until_renewal === 1 ? "" : "s"}`}</div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-slate-400 mt-1">{new Date(r.current_period_end * 1000).toLocaleDateString("en-AU", { dateStyle: "medium" })}</div>
                  <div className="mt-2 text-sm text-slate-800">{r.buyer_name || "-"}</div>
                  <a href={`mailto:${r.buyer_email}`} className="text-xs text-brand-blue hover:underline">{r.buyer_email || "-"}</a>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    {r.renewal_nudge_sent_at
                      ? <span data-testid={`nudge-sent-${r.session_id}`} className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Thank-you sent</span>
                      : <button
                          data-testid={`nudge-btn-${r.session_id}`}
                          onClick={() => sendRenewalNudge(r)}
                          disabled={busy[`nudge-${r.session_id}`] || !r.buyer_email}
                          className="text-[10px] uppercase tracking-widest font-semibold text-brand-orange hover:text-brand-blue disabled:opacity-50 inline-flex items-center gap-1"
                        >
                          {busy[`nudge-${r.session_id}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Heart className="w-3 h-3" /> Send thank-you</>}
                        </button>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {upcomingCoaching.length > 0 && (
          <section data-testid="admin-upcoming-coaching" className="mb-10 bg-brand-blue text-white p-6 border border-brand-blue">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-brand-yellow mb-4"><Calendar className="w-3.5 h-3.5" /> Upcoming coaching sessions</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {upcomingCoaching.map((o) => (
                <div key={o.id} data-testid={`upcoming-${o.id}`} className="bg-white/10 border border-white/20 p-4 rounded-sm">
                  <div className="font-serif text-lg">{new Date(o.scheduled_at).toLocaleString("en-AU", { dateStyle: "full", timeStyle: "short" })}</div>
                  <div className="text-sm text-white/85 mt-1">{o.product_title}</div>
                  <div className="text-xs text-white/70 mt-1">{o.buyer_name || "-"} · <a href={`mailto:${o.buyer_email}`} className="underline hover:text-brand-yellow">{o.buyer_email}</a></div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Filter bar */}
        <div data-testid="admin-filter-bar" className="bg-white border border-slate-200 p-4 mb-6 flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, buyer, email, order id, note…" className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 focus:outline-none focus:border-brand-blue" />
          </div>
          <div><label className="block text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">From</label><input data-testid="admin-filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="text-sm border border-slate-300 px-2 py-2 focus:outline-none focus:border-brand-blue" /></div>
          <div><label className="block text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">To</label><input data-testid="admin-filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="text-sm border border-slate-300 px-2 py-2 focus:outline-none focus:border-brand-blue" /></div>
          <div><label className="block text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Kind</label>
            <select data-testid="admin-filter-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="text-sm border border-slate-300 px-2 py-2 focus:outline-none focus:border-brand-blue">
              <option value="all">All</option><option value="book">Book</option><option value="coaching">Coaching</option><option value="subscription">Subscriptions</option>
            </select>
          </div>
          {filtered && <button data-testid="admin-filter-clear" onClick={clearFilters} className="text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-brand-orange px-2 py-2">Clear</button>}
        </div>

        {state.loading ? <div className="py-24 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading orders…</div> : (
          <>
            <section data-testid="admin-shop-orders" className="mb-14">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-2xl text-brand-blue">Shop orders</h2>
                <div data-testid="shop-orders-counter" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{filteredOrders.length}{filtered ? ` of ${orders.length}` : ""} total</div>
              </div>
              {filteredOrders.length === 0 ? (
                <div className="bg-white border border-slate-200 p-8 text-center text-slate-500 text-sm">No matching shop orders.</div>
              ) : (
                <div className="bg-white border border-slate-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest font-semibold text-slate-500 border-b border-slate-200">
                        <th className="px-4 py-3">Date</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Buyer</th>
                        <th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 min-w-[180px]">Session / Fulfillment</th>
                        <th className="px-4 py-3 min-w-[200px]">Note</th><th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((o) => (
                        <tr key={o.id} data-testid={`admin-order-row-${o.id}`} className="border-b border-slate-100 hover:bg-slate-50/50 align-top">
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{fmtDate(o.created_at)}</td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-800">{o.product_title}</div>
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mt-0.5">{o.product_kind || "-"} · {o.id}</div>
                          </td>
                          <td className="px-4 py-3"><div className="text-slate-800">{o.buyer_name || "-"}</div><a href={`mailto:${o.buyer_email}`} className="text-xs text-brand-blue hover:underline">{o.buyer_email || "-"}</a></td>
                          <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{money(o.amount_cents, o.currency)}</td>
                          <td className="px-4 py-3"><StatusBadge status={o.payment_status} refunded={o.refunded} /></td>
                          <td className="px-4 py-3">
                            {o.product_kind === "coaching"
                              ? <ScheduleCell order={o} onSaved={load} />
                              : <span className="text-xs text-slate-600">{o.fulfillment_status || "-"}</span>}
                          </td>
                          <td className="px-4 py-3">
                            <NotesCell
                              initialValue={o.admin_note}
                              endpoint={`${API}/admin/orders/${o.id}/notes`}
                              testIdPrefix={`admin-note-${o.id}`}
                              placeholder="Add note…"
                              onSaved={load}
                            />
                          </td>
                          <td className="px-4 py-3 text-right">
                            {o.payment_status === "paid" && !o.refunded
                              ? <button data-testid={`admin-refund-order-btn-${o.id}`} onClick={() => refundOrder(o)} disabled={busy[o.id]} className="text-xs uppercase tracking-widest font-semibold text-red-700 hover:text-red-900 disabled:opacity-50">{busy[o.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Refund"}</button>
                              : <span className="text-xs text-slate-400">-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section data-testid="admin-subscriptions">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-2xl text-brand-blue">Member Hub subscriptions</h2>
                <div data-testid="subs-counter" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{filteredSubs.length}{filtered ? ` of ${subs.length}` : ""} total</div>
              </div>
              {filteredSubs.length === 0 ? (
                <div className="bg-white border border-slate-200 p-8 text-center text-slate-500 text-sm">No matching subscriptions.</div>
              ) : (
                <div className="bg-white border border-slate-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest font-semibold text-slate-500 border-b border-slate-200">
                        <th className="px-4 py-3">Date</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Member</th>
                        <th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Tier</th>
                        <th className="px-4 py-3 min-w-[200px]">Private note</th>
                        <th className="px-4 py-3 text-right min-w-[220px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubs.map((s) => {
                        const isPaid = s.payment_status === "paid" && !s.refunded;
                        const isCanceling = !!s.cancel_at_period_end;
                        const isActive = s.subscription_status === "active" && isPaid;
                        return (
                        <tr key={s.session_id} data-testid={`admin-sub-row-${s.session_id}`} className="border-b border-slate-100 hover:bg-slate-50/50 align-top">
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{fmtDate(s.created_at)}</td>
                          <td className="px-4 py-3"><div className="font-semibold text-slate-800">{s.lookup_key === "member_yearly" ? "Annual" : "Monthly"}</div><div className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mt-0.5">{s.lookup_key || "-"}</div></td>
                          <td className="px-4 py-3"><div className="text-slate-800">{s.buyer_name || "-"}</div><a href={`mailto:${s.buyer_email}`} className="text-xs text-brand-blue hover:underline">{s.buyer_email || "-"}</a></td>
                          <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{money(s.amount, s.currency)}</td>
                          <td className="px-4 py-3"><StatusBadge status={s.payment_status} refunded={s.refunded} /></td>
                          <td className="px-4 py-3 text-xs text-slate-600">
                            {s.tier || "-"} {s.subscription_status ? `· ${s.subscription_status}` : ""}
                            {isCanceling && <div className="text-amber-700 mt-0.5">Ends {s.cancel_period_end_ts ? new Date(s.cancel_period_end_ts * 1000).toLocaleDateString("en-AU") : "period end"}</div>}
                          </td>
                          <td className="px-4 py-3">
                            {s.user_id
                              ? <NotesCell initialValue={s.member_note} endpoint={`${API}/admin/users/${s.user_id}/note`} testIdPrefix={`member-note-${s.user_id}`} placeholder="Private note…" onSaved={load} />
                              : <span className="text-xs text-slate-400">-</span>}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {isActive && !isCanceling && (
                              <button data-testid={`admin-cape-sub-btn-${s.session_id}`} onClick={() => cancelAtPeriodEnd(s)} disabled={busy[`cape-${s.session_id}`]} className="text-xs uppercase tracking-widest font-semibold text-slate-600 hover:text-brand-orange disabled:opacity-50 inline-flex items-center gap-1 mr-4" title="Cancel at end of current billing period. Member keeps access until then.">
                                {busy[`cape-${s.session_id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CalendarX className="w-3.5 h-3.5" /> Cancel at period end</>}
                              </button>
                            )}
                            {isActive ? <button data-testid={`admin-refund-sub-btn-${s.session_id}`} onClick={() => refundSub(s)} disabled={busy[s.session_id]} className="text-xs uppercase tracking-widest font-semibold text-red-700 hover:text-red-900 disabled:opacity-50">{busy[s.session_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Refund & cancel"}</button> : <span className="text-xs text-slate-400">-</span>}
                          </td>
                        </tr>);
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {/* Digest Preview Modal */}
      {digestPreview && (
        <div data-testid="digest-preview-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setDigestPreview(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div>
                <div className="text-xs uppercase tracking-widest font-semibold text-brand-orange">Weekly digest preview</div>
                <div className="font-serif text-xl text-brand-blue mt-1">{digestPreview.subject}</div>
                <div className="text-xs text-slate-500 mt-1">Will be sent to <strong>{digestPreview.recipient}</strong></div>
              </div>
              <button data-testid="digest-preview-close" onClick={() => setDigestPreview(null)} className="text-slate-500 hover:text-brand-orange p-1"><X className="w-5 h-5" /></button>
            </div>
            <div data-testid="digest-preview-html" className="p-6 overflow-y-auto flex-1 bg-slate-50" dangerouslySetInnerHTML={{ __html: digestPreview.html }} />
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 bg-white">
              <button onClick={() => setDigestPreview(null)} className="text-xs uppercase tracking-widest font-semibold text-slate-600 hover:text-brand-orange px-3 py-2">Cancel</button>
              <button data-testid="digest-preview-send" onClick={sendDigestNow} disabled={busy.digest} className="btn-primary disabled:opacity-50">
                {busy.digest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
